import { Component, Input, OnInit } from '@angular/core';
import { RequirementService } from '@/services/requirement/requirement.service';
import { Document } from '@/interfaces/requirement';
import { FormArray, FormControl, FormGroup, AbstractControl, Validators } from '@angular/forms';
import { CommonService } from '@/services/common/common.service';
import { MineDocumentsStatus } from '@/interfaces/common/common.interface';
import { normalize } from '@/utils/normalizeString';
import { dateEndMustBeAfterDateStart } from '@/validators/dateEndMustBeAfterDateStart';
import { TaskCategories } from '@/enums/tasks';

interface MineDocument extends Document {
  isEnabled: boolean;
  disabledMessage?: string;
}

interface MineDocumentFormGroup extends Omit<FormGroup, 'controls'> {
  controls: {
    id: FormControl;
    documentRequiredTypeId: FormControl;
    documentStatusId: FormControl;
    dateFrom: FormControl;
    dateUntil: FormControl;
    dateFromDefault: FormControl;
    dateUntilDefault: FormControl;
    index: FormControl;
    isEnabled: FormControl;
  }
}

@Component({
  selector: 'app-mine-requirement-table',
  templateUrl: './mine-requirement-table.component.html',
  styleUrls: ['./mine-requirement-table.component.css'],
})
export class MineRequirementTableComponent implements OnInit {
  @Input() requirementId?: number = 0;
  @Input() formArray: FormArray = new FormArray([]);

  @Input()
  get documents(): Document[] | undefined {
    return this._documents;
  }
  set documents(documents: Document[] | undefined) {
    if (documents) {
      this.drawDocuments(documents);
      this._documents = documents;
    }
  }
  private _documents: Document[] = [];

  mineDocumentsStatus: MineDocumentsStatus[] = [];

  mineDocuments: MineDocument[] = [];
  formPreparationState: Record<string, boolean> = { form: false, date: false };

  constructor(
    private requirementService: RequirementService,
    private commonService: CommonService,
  ) {

  }

  ngOnInit(): void {
    if (this.requirementId) {
      this.getMineRequirementToDraw(this.requirementId);
    }
  }

  getMineRequirementToDraw(requirementId: number) {
    this.requirementService.getDetailsMine(requirementId)
      .subscribe({
        next: (details) => {
          let processedDetails = details;

          const everyDocumentMineIsEmpty = details.documents.every((x) => !(x.dateFrom || x.dateUntil));

          const defaultDateFrom = new Date();
          defaultDateFrom.setDate(defaultDateFrom.getDate() - 30);
          const defaultDateUntil = new Date();
          if (details.requirementTypeId === TaskCategories.RENOVACION && everyDocumentMineIsEmpty) {
            processedDetails = {
              ...details,
              documents: details.documents.map((doc, index) => {
                if (index !== details.documents.length - 1) {
                  return { ...doc, dateFrom: defaultDateFrom, dateUntil: defaultDateUntil, documentStatusId: 2 };
                }
                return doc;
              }),
            };
          }

          this.drawDocuments(processedDetails.documents);
        },
      });
  }

  drawDocuments(documents: Document[]) {
    for (let index = 0; index < this.formArray.controls.length; index++) {
      this.formArray.removeAt(index);
    }
    this.formPreparationState.form = false;
    this.formPreparationState.data = false;
    this.commonService.getMineDocumentsStatus().subscribe({
      next: (mineDocumentsStatus) => {
        this.mineDocumentsStatus = mineDocumentsStatus;
        const newDocuments: MineDocument[] = [];
        documents.reduce<FormGroup[]>((acc, selectedDocument, index) => {
          let newDocument: Partial<MineDocument> = {};
          const status = mineDocumentsStatus.find((docStatus) => selectedDocument.documentStatusId === docStatus.id);
          if (index === 0) {
            newDocument = { ...selectedDocument, isEnabled: true };
          }
          else if (status) {
            const previousStatusId = documents[index - 1].documentStatusId;
            const previousStatus = mineDocumentsStatus.find((docStatus) => previousStatusId === docStatus.id);

            if (previousStatus) {
              if (normalize(previousStatus.name) === normalize('Completado')) {
                newDocument = { ...selectedDocument, isEnabled: true };
              }
              else {
                newDocument = {
                  ...selectedDocument,
                  isEnabled: false,
                };
              }
            }
          }
          else {
            newDocument = { ...document, isEnabled: false, disabledMessage: '' };
          }
          newDocuments.push(<MineDocument>newDocument);

          const documentId = selectedDocument.id;
          const controls: Record<string, FormControl> = {
            id: new FormControl(documentId), // Estos se dejan dentro del control para hacer validaciones
            documentRequiredTypeId: new FormControl(selectedDocument.documentRequiredTypeId), // Estos se dejan dentro del control para hacer validaciones
            documentStatusId: new FormControl(selectedDocument.documentStatusId, [Validators.required]),
            dateFrom: new FormControl(selectedDocument.dateFrom),
            dateUntil: new FormControl(selectedDocument.dateUntil),
            dateFromDefault: new FormControl(selectedDocument.dateFrom),
            dateUntilDefault: new FormControl(selectedDocument.dateUntil),
            index: new FormControl(index), // Estos se dejan dentro del control para hacer validaciones
            isEnabled: new FormControl(newDocument.isEnabled),
          };

          if (newDocument.isEnabled) {
            controls.dateFrom = new FormControl(selectedDocument.dateFrom, [Validators.required]);
            controls.dateUntil = new FormControl(selectedDocument.dateUntil, [Validators.required]);
          }

          const formGroup = new FormGroup(controls);

          if (newDocument.isEnabled) {
            formGroup.setValidators(dateEndMustBeAfterDateStart('dateFrom', 'dateUntil'));
          }

          acc.push(formGroup);

          if (index === (documents.length - 1)) {
            acc.map((formGroupSaved) => {
              this.formArray.push(formGroupSaved);
            });
            this.formPreparationState.form = true;
          }

          return acc;
        }, []);

        const mineDocuments = <MineDocument[]>newDocuments;

        this.formArray.controls.map((fg) => {
          const formGroup: MineDocumentFormGroup = <unknown>fg as MineDocumentFormGroup;
          formGroup.controls.documentStatusId.valueChanges.subscribe({
            next: (value) => {
              let mustBeBlocked = false;
              for (let index = 1; index < this.formArray.controls.length; index++) {
                if (mineDocuments[index].isEnabled === true) {
                  // status 1: pendiente; 2: completado; 3: rechazado
                  const previousControl = <unknown>this.formArray.at(index - 1) as MineDocumentFormGroup;
                  const control = <unknown>this.formArray.at(index) as MineDocumentFormGroup;
                  const previousStatusId = +previousControl.controls.documentStatusId.value;

                  if (previousStatusId !== 2) {
                    mustBeBlocked = true;
                  }

                  if (mustBeBlocked) {
                    control.patchValue({
                      isEnabled: false,
                      dateFrom: new Date(2016, 11, 17),
                      dateUntil: new Date(2016, 11, 18),
                    });
                  }
                  else {
                    control.patchValue({
                      dateFrom: mineDocuments[index].dateFrom,
                      dateUntil: mineDocuments[index].dateUntil,
                      isEnabled: true,
                    });
                  }
                }
              }
            },
          });
        });

        this.mineDocuments = mineDocuments;
        this.formPreparationState.data = true;
      },
    });

  }

  getformGroupByRowIndex(index: number): FormGroup {
    let requestedIndex = -1;
    this.formArray.value.some(({ index: rowIndex }: any, formArrayIndex: number) => {
      if (rowIndex === index) {
        requestedIndex = formArrayIndex;
      }
    });
    return this.formArray.at(requestedIndex) as FormGroup;
  }

  asFormControl(abstractControl: AbstractControl): FormControl {
    return abstractControl as FormControl;
  }

  documentDateFromError(index: number): string {
    const formGroupControl = this.getformGroupByRowIndex(index);

    const error = formGroupControl.controls.dateFrom.errors;
    if (error) {
      if (error.description) {
        return error.description;
      }
    }
    return '';
  }

  documentDateRangeError(index: number): string {
    const formGroupControl = this.getformGroupByRowIndex(index);

    const error = formGroupControl.errors;
    if (error) {
      if (error.description) {
        return error.description;
      }
    }
    return '';
  }

  getDisabledMessage(index: number) {
    if (this.mineDocuments.length > 0) {
      const previousDocumentName = this.mineDocuments[index - 1].description;
      const { description } = this.mineDocuments[index];
      return `Una vez completado "${previousDocumentName}" se podrá registrar "${description}".`;
    }
    return '';
  }

  get isFormReady(): boolean {
    return this.formPreparationState.form && this.formPreparationState.data;
  }
}
